import type { BruktbilFilters } from './useBruktbilFilters'

/**
 * URL for a klasse's detail page, carrying the current filter state.
 *
 * Shared by the Klasser tab's table and the Oversikt tab's klasse grid (#479)
 * so the two links cannot drift: a detail page reached from either row must
 * show the same segment the row was counted from. These are *URL* params
 * (`from`/`to`), which useBruktbilFilters reads back — not the API's
 * `from_date`/`to_date`, which buildBruktbilQs handles.
 *
 * geo/geoDir are included: with the postnummer filter on, dropping them would
 * land the user on a detail page counting a different population than the row
 * they clicked.
 */
export type DetaljerFilters = Pick<
  BruktbilFilters,
  'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering' | 'postnummerEnabled' | 'postnummerDirection'
>

function filterQs(f: DetaljerFilters): URLSearchParams {
  const qs = new URLSearchParams()
  qs.set('from', f.from)
  qs.set('to', f.to)
  for (const k of f.klasse) qs.append('klasse', k)
  // varebil is 3-state: undefined means "alle" and must stay absent.
  if (f.varebil != null) qs.set('varebil', String(f.varebil))
  if (f.omregistrering) qs.set('omregistrering', 'true')
  if (f.postnummerEnabled) {
    qs.set('geo', '1')
    if (f.postnummerDirection && f.postnummerDirection !== 'begge') {
      qs.set('geoDir', f.postnummerDirection)
    }
  }
  return qs
}

export function klasseDetaljerUrl(classLabel: string, f: DetaljerFilters): string {
  return `/bruktbil/klasser/${encodeURIComponent(classLabel)}/detaljer?${filterQs(f)}`
}

/** Markedsandel per forhandler for one merke (#489 AC5). */
export function merkeDetaljerUrl(merke: string, f: DetaljerFilters): string {
  return `/bruktbil/merke-modell/${encodeURIComponent(merke)}/detaljer?${filterQs(f)}`
}

/**
 * Same page for one (merke, modell) pair (#489 AC6). Both names are in the
 * path because SVV modell strings collide across brands — "3" is both a MAZDA
 * and a BMW — so a modell-only route would land on a mix of two.
 */
export function modellDetaljerUrl(merke: string, modell: string, f: DetaljerFilters): string {
  return (
    `/bruktbil/merke-modell/${encodeURIComponent(merke)}` +
    `/${encodeURIComponent(modell)}/detaljer?${filterQs(f)}`
  )
}
